import type { CardReading } from './ReadCard';

/**
 * WHAT TO CHECK AFTER A PHOTO, NAMED. The reading was right on about 98 cells in 100 in the trial,
 * and every card it got wrong broke a rule the grid already checks — but "check every number" is
 * still the instruction, because she is the one holding the card.
 *
 * `totals` is the grid's, recomputed as she types, so a line disappears the moment she corrects the
 * cell behind it.
 */
export function ReadingNotice({
  reading,
  totals,
  nameOf,
  then,
}: {
  reading: CardReading;
  totals: Map<string, { points: number }>;
  nameOf: (playerId: string | null) => string | null;
  /** What she does next: "save" for the director, "hand it in" for a player. */
  then: string;
}) {
  const totalsDisagree = reading.writtenTotals.filter((w) => (totals.get(w.playerId)?.points ?? 0) !== w.total);

  return (
    <div role="status" className="max-w-[34rem] rounded-md border border-light bg-sand px-4 py-3 text-[14px] text-ink-2">
      {reading.blank && reading.games.length === 0 ? (
        <p>The card looks blank. Nothing was filled in.</p>
      ) : (
        <p className="font-medium text-ink">Read from the photo. Check each number against the card, then {then}.</p>
      )}
      {reading.uncertain.length + totalsDisagree.length + reading.unmatchedNames.length > 0 ? (
        <ul className="mt-2 flex list-disc flex-col gap-1 pl-5">
          {reading.uncertain.map((u, index) => (
            <li key={`u${index}`}>
              {[nameOf(u.playerId), u.gameNumber ? `game ${u.gameNumber}` : null].filter(Boolean).join(', ')}
              {u.playerId || u.gameNumber ? ': ' : ''}
              {u.note}
            </li>
          ))}
          {totalsDisagree.map((w) => (
            <li key={`t${w.playerId}`}>
              The card&rsquo;s total for {nameOf(w.playerId)} is {w.total}; the games here come to{' '}
              {totals.get(w.playerId)?.points ?? 0}.
            </li>
          ))}
          {reading.unmatchedNames.map((name) => (
            <li key={`n${name}`}>&ldquo;{name}&rdquo; on the card is nobody seated at this table.</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** The cells a reading was unsure of, as `playerId:gameNumber`. */
export function doubtfulCells(reading: CardReading | null): Set<string> {
  return new Set(
    (reading?.uncertain ?? []).filter((u) => u.playerId && u.gameNumber).map((u) => `${u.playerId}:${u.gameNumber}`),
  );
}
